import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageForm from "components/pageForm";
import Indicator from "pages/createMission/indicator";
import FooterButton from "pages/createMission/footer.button";
import Button from "components/button";
import CreateMissionSubNav from "components/subNav/createMission.subNav";
import FormsCreate from "pages/createMission/formsCreate";
import UseContext from "hooks/useContext";
import ApiDatabase from "server";
import { toast } from "react-toastify";
import useModal from "../components/modal/useModal";
import { dateToEpoch, dateToIsoString } from "../helper/helper";
import { toToolNames } from "pages/missionTemplates/utils/templateTools";

const VALID_PAGES = [3, 5, 7, 10, 11];
const PAGE_ORDER = [3, 5, 7, 10];

const hasLocation = (location) => {
    if (!location) return false;
    if (typeof location === "string") return location.trim() !== "";
    return Object.keys(location).length > 0;
};

const resetMissionData = {
    activity: "",
    qualification: "",
    tools: [],
    licences: [],
    languages: [],
    contact_name: "",
    contact_poste: "",
    location: "",
    dateStart: "",
    dateEnd: "",
    schedule: [],
    isScheduleCheck: true,
    isConfirmedSchedule: false,
    isScheduleRequired: false,
    users_invite: [],
    motif: "",
    other: "",
    other_interim: "",
    salary: "",
};

const nextPageFrom = (page, validPages) => {
    const idx = validPages.indexOf(page);
    return validPages[idx + 1] ?? page;
};

const prevPageFrom = (page, validPages) => {
    const idx = validPages.indexOf(page);
    if (idx <= 0) return page;
    return validPages[idx - 1] ?? page;
};

const CreateOfMission = () => {
    const navigate = useNavigate();
    const { toggle } = useModal();
    const {
        page,
        setPage,
        data,
        setData,
        companyData,
        isDateValid,
        selectedMissionTemplate,
        setSelectedMissionTemplate,
    } = UseContext();

    const [isSubmitLoading, setIsSubmitLoading] = React.useState(false);
    const validPages = VALID_PAGES;
    const pageOrder = PAGE_ORDER;
    const firstPage = validPages[0];

    useEffect(() => {
        if (!validPages.includes(page)) {
            setPage(firstPage);
        }
    }, [firstPage, page, setPage, validPages]);

    useEffect(() => {
        if (page === 11) return;
        if (!selectedMissionTemplate?._id) {
            navigate("/missionTemplates", { replace: true });
            toast.info(
                "Choisissez une fiche de poste depuis la liste pour démarrer une mission."
            );
        }
    }, [selectedMissionTemplate, navigate, page]);

    // Hydrate `data` depuis la fiche sélectionnée.
    useEffect(() => {
        if (!selectedMissionTemplate) return;
        const t = selectedMissionTemplate;
        setData((prev) => ({
            ...prev,
            activity: t.activity || "",
            qualification: t.title || t.qualification || "",
            tools: toToolNames(t.tools),
            licences: t.licences || [],
            languages: t.language || [],
            contact_name: t.contact?.name || "",
            contact_poste: t.contact?.poste || "",
            location: t.location || "",
            other: t.other || "",
            other_interim: t.other_interim || "",
            salary:
                typeof t.salary?.wage_per_hour === "number"
                    ? String(t.salary.wage_per_hour)
                    : "",
        }));
    }, [selectedMissionTemplate, setData]);

    const handlePrev = () => {
        if (page === firstPage) {
            navigate("/missionTemplates");
            return;
        }
        setPage(prevPageFrom(page, validPages));
    };
    const handleNext = () => setPage(nextPageFrom(page, validPages));

    const handleSubmit = (e) => {
        setIsSubmitLoading(true);
        e.preventDefault();
        if (!selectedMissionTemplate?._id) {
            setIsSubmitLoading(false);
            toast.error("Veuillez sélectionner une fiche de poste");
            return;
        }
        const idCompany = companyData._id;

        if (data.schedule.length > 0) {
            for (let i = 0; i < data.schedule.length; i++) {
                if (data.schedule[i].days.length > 0) {
                    for (let j = 0; j < data.schedule[i].days.length; j++) {
                        if (data.schedule[i].days[j].date) {
                            data.schedule[i].days[j].date = dateToIsoString(
                                new Date(data.schedule[i].days[j].date)
                            );
                        }
                    }
                }
            }
        }

        const dataToSend = {
            ...data,
            dateStart: dateToIsoString(data.dateStart),
            dateEnd: dateToIsoString(data.dateEnd),
        };

        ApiDatabase.postCreateMission(
            {
                dataToSend,
                idCompany,
                isDesktop: true,
                ...(selectedMissionTemplate?._id
                    ? { missionTemplateId: selectedMissionTemplate._id }
                    : {}),
            },
            () => {
                setData(resetMissionData);
                setSelectedMissionTemplate(null);
                handleNext();
                setIsSubmitLoading(false);
            },
            (err) => {
                console.log(err);
                setIsSubmitLoading(false);
                toast.error(
                    err?.response?.data?.error || "Création impossible"
                );
            }
        );
    };

    const conditionMotif = (motifData) => {
        switch (motifData?.type) {
            case "1":
                return motifData.message !== "";
            case "2":
                return (
                    motifData.date !== "" &&
                    motifData.name !== "" &&
                    motifData.prenom !== ""
                );
            case "3":
                return (
                    motifData.message !== "" &&
                    motifData.name !== "" &&
                    motifData.prenom !== "" &&
                    motifData.poste !== ""
                );
            case "4":
                return motifData.message !== "";
            case "5":
                return motifData.message !== "";
            default:
                return false;
        }
    };

    const validateSchedule = () => {
        if (!data.isScheduleCheck) return true;

        if (data.schedule.length > 0) {
            let isValidSchedule = false;
            let isScheduleNotComplete = false;
            for (let i = 0; i < data.schedule.length; i++) {
                for (let j = 0; j < data.schedule[i]?.days?.length; j++) {
                    if (data.schedule[i].days[j].periods) {
                        if (
                            data.schedule[i].days[j].periods
                                .periode_one_start !== "" &&
                            data.schedule[i].days[j].periods
                                .periode_one_end !== ""
                        ) {
                            isValidSchedule = true;
                        } else {
                            isScheduleNotComplete = true;
                        }
                    } else {
                        isScheduleNotComplete = true;
                    }
                }
            }
            if (isValidSchedule) {
                if (!isScheduleNotComplete || data.isConfirmedSchedule) {
                    return true;
                }
                toggle("cM_confirmSchedule");
                return false;
            }
        }

        if (data.isScheduleRequired) {
            toast.error("Veuillez remplir le planning");
        } else {
            toast.error(
                "Veuillez remplir le planning ou retirer l'ajout d'un planning à la mission"
            );
        }
        return false;
    };

    const handlenxt = () => {
        switch (page) {
            case 1:
                if (!data.activity || !data.qualification) {
                    toast.error("Veuillez sélectionner secteur et qualification");
                    return;
                }
                handleNext();
                break;
            case 2:
                handleNext();
                break;
            case 3:
                if (data.motif === "") {
                    toast.error("Veuillez sélectionner un motif");
                    return;
                }
                if (!conditionMotif(data?.motif)) {
                    toast.error(
                        "Veuillez remplir les champs du motif sélectionné"
                    );
                    return;
                }
                handleNext();
                break;
            case 4:
                if (!hasLocation(data.location)) {
                    toast.error("Veuillez renseigner une adresse");
                    return;
                }
                if (
                    !data.dateStart ||
                    !data.dateEnd ||
                    !isDateValid
                ) {
                    toast.error(
                        "Veuillez renseigner les dates de la mission avec une période valide"
                    );
                    return;
                }
                handleNext();
                break;
            case 5:
                if ((
                    !data.dateStart ||
                    !data.dateEnd ||
                    !isDateValid
                )) {
                    toast.error(
                        "Veuillez renseigner les dates de la mission avec une période valide"
                    );
                    return;
                }
                if (validateSchedule()) {
                    handleNext();
                }
                break;
            case 6:
                if (!data.salary) {
                    toast.error("Veuillez renseigner un taux horaire");
                    return;
                }
                handleNext();
                break;
            case 7:
                handleNext();
                break;
            case 8:
                if (!data.contact_name || !data.contact_poste) {
                    toast.error("Veuillez compléter les informations responsable");
                    return;
                }
                handleNext();
                break;
            case 9:
                handleNext();
                break;
            case 10:
                handleNext();
                break;
            default:
                break;
        }
    };

    const showFooter = page !== 11;
    const showBack = page !== 11 && page !== firstPage;
    const stepIndex =
        page === 11
            ? pageOrder.length
            : pageOrder.indexOf(page) + 1;

    if (!selectedMissionTemplate?._id && page !== 11) {
        return null;
    }

    return (
        <>
            <PageForm>
                <div className="col-span-12 bg-white sticky z-10 top-16">
                    <CreateMissionSubNav />
                </div>
                <div className="col-span-full pb-24">
                    <div className="w-full mx-auto lg:w-[800px] px-6 py-8">
                        <FormsCreate />
                    </div>
                </div>

                {showFooter && (
                    <div className="col-span-full">
                        <div className="fixed bottom-0 left-[80px] sidebar-expanded:left-64 2xl:left-64 right-0 bg-white border-t border-gray-100 z-20">
                            <div className="w-full mx-auto lg:w-[800px] px-6 py-4">
                                <div className="flex justify-between items-center gap-4">
                                    <Indicator
                                        formIndex={stepIndex}
                                        totalSteps={pageOrder.length}
                                    />
                                    <div className="flex gap-3 shrink-0">
                                        {showBack ? (
                                            <Button
                                                size={"LG"}
                                                color={"SECONDARY"}
                                                onClick={handlePrev}
                                            >
                                                Retour
                                            </Button>
                                        ) : null}
                                        <FooterButton
                                            oC1={handlenxt}
                                            oC2={handleSubmit}
                                            isLoading={isSubmitLoading}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </PageForm>
        </>
    );
};

export default CreateOfMission;
